import * as React from 'react';
import { List, IconButton } from '@mui/material';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { ReusableTrendsCardHeader } from './ReusableTrendsCardHeader';
import { Data, isDataLoading, isDataLoaded } from '../../types/data-providers';
import { TrendsGraphContentLoader } from './ReusableTrendCardContentLoader';
import { useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ChartColors } from './colors';
import * as _ from 'lodash';
import {
    getChartsLanguage,
    getChartsAxisDateTimeLabelFormats,
    getChartsTooltipDateTimeLabelFormats,
} from '../../data/translations/i18n';
import * as PXBColors from '@brightlayer-ui/colors';
import { ZoomOutButton } from './ZoomOutButton';
import { DataEmptyIndicator } from '../DataEmptyIndicator';
import { Icon } from '../../lib/icons';
import { Clock } from '../../lib/clock';
import { getPrecisionNumber } from '../../lib/precision-helper';
import { parseFloat } from '../../lib';
import { TrendExportModal } from '../DeviceTrends/TrendExportModal';
import alarmImg from '../../assets/images/alarmImg.png';
import { DeviceMasterProvider } from '../../data/DataProviders/DeviceMasterProvider';
import { TrendDataProvider } from '../../data/DataProviders/TrendDataProvider';

// Initialize Highcharts modules
require('highcharts/modules/annotations')(Highcharts);
require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);

const getTimezoneOffset = (): number => Clock.now().getTimezoneOffset();

export const ReusableTrendsGraph: React.FC<{
    large?: boolean;
    count?: number;
    isDevice?: boolean;
    refresh?: { isDownload: boolean; type: '' | 'application/pdf' | 'image/png' };
    setRefresh?: Function;
    Inactive?: boolean;
    exportChannel?: Data<
        Array<{
            id: number;
            channelName: string;
            deviceId: string;
            deviceName: string;
            value: Array<{
                dateTime: string;
                value: string;
                isNewAlarm?: boolean;
                alarmCount?: number;
            }>;
            unit?: string | null;
            color?: any;
        }>
    >;
    trendsData: Data<
        Array<{
            id: number;
            channelName: string;
            deviceId: string;
            deviceName: string;
            value: Array<{
                dateTime: string;
                value: string;
                isNewAlarm?: boolean;
                alarmCount?: number;
            }>;
            unit?: string | null;
            color?: any;
        }>
    >;
    style?: React.CSSProperties;
    fetchTrendData?: Function;
    downloadCSV: Function;
    channels?: {
        maxTrendChannelToBeAllowedFromUser: number;
        trendChannels: Array<{ id: string; channelName: string; wasPriority: string | null }>;
    };
    deviceId?: string;
    downloadFileName?: string;
    disableDownload?: Function;
    tickInterval?: any;
    pdiBellIcon?: any;
    siteId?: any;
}> = (props) => {
    const { t, formatDateTime } = useLanguageLocale();
    const trendLinesEnabled = useSelector(Selectors.Dashboard.trendLinesEnabled);
    const hoveredTrendline = useSelector(Selectors.Dashboard.hoveredTrendline);
    const chartRef = React.useRef<{
        chart: Highcharts.Chart;
        annotation: Highcharts.Annotation;
        container: React.RefObject<HTMLDivElement>;
    }>(null);
    const [open, setOpen] = React.useState(false);
    React.useEffect(() => {
        const chartsLanguage = getChartsLanguage();
        if (chartsLanguage) {
            Highcharts.setOptions({
                lang: chartsLanguage,
            });
        }
    });

    const [zoomOutButtonVisible, setZoomOutButtonVisible] = React.useState(false);

    function afterSetExtremes(this: Highcharts.Axis, evt: Highcharts.AxisSetExtremesEventObject): void {
        if (evt.userMin || evt.userMax) {
            setZoomOutButtonVisible(true);
        }
    }

    const handleClickOpen = (): void => {
        setOpen(true);
    };
    const handleClose = (): void => {
        setOpen(false);
    };

    const someChannelsEnabled = React.useMemo(() => {
        if (!isDataLoaded(props.trendsData)) {
            return true;
        }
        return props.trendsData;
        //return props.trendsData.value.some((trend) => trendLinesEnabled[trend.id] !== false);
    }, [props.trendsData]);

    const { hasDataPoints, highchartsConfig } = React.useMemo((): {
        hasDataPoints: boolean | undefined;
        highchartsConfig: Highcharts.Options | undefined;
    } => {
        if (!isDataLoaded(props.trendsData)) {
            return {
                hasDataPoints: undefined,
                highchartsConfig: undefined,
            };
        }
        const trends = props.trendsData.value.map((trend, index) => ({ ...trend, index }));
        const bellDataPoint: any = [];
        props.trendsData.value.map((item) => {
            item.value.map((i) => {
                bellDataPoint.push(i);
            });
        });

        const dataPoints = trends.map((trend) => ({
            label: trend.channelName,
            data: trend.value
                .sort((a: any, b: any) => new Date(a.dateTime).valueOf() - new Date(b.dateTime).valueOf())
                .map((value) => {
                    const numberValue = parseFloat(value.value);
                    return [
                        new Date(value.dateTime).getTime(),
                        numberValue !== null ? getPrecisionNumber(value.value) : null,
                    ];
                })
                .filter((value) => value[1] !== null),
            index: props.Inactive ? trend.id : trend.index,
            unit: trend.unit || '',
            channelId: trend.id,
            deviceId: trend.deviceId,
            deviceName: trend.deviceName,
            color: trend.color,
        }));
        const hoveredTrend =
            hoveredTrendline !== undefined
                ? trends.find(
                      (trend) => trend.id === hoveredTrendline.channelId && trend.deviceId === hoveredTrendline.deviceId
                  )
                : undefined;

        const hoveredUnit = hoveredTrend !== undefined ? hoveredTrend.unit || '' : undefined;

        const yAxis = _.uniq(trends.map((trend) => trend.unit || '')).map(
            (unit): Highcharts.YAxisOptions => ({
                labels: {
                    format: `{value}${unit}`,
                    style: {
                        fontFamily: 'Open Sans',
                        fontWeight: hoveredUnit === unit ? '700' : undefined,
                    },
                },
                tickInterval: 5,
                gridLineColor:
                    hoveredUnit === unit
                        ? PXBColors.black[50]
                        : hoveredTrend === undefined
                        ? PXBColors.black[50]
                        : undefined,
                title: {
                    text: undefined,
                },
            })
        );

        return {
            hasDataPoints:
                //props.isDevice
                // ? dataPoints.some(
                //       (dp) =>
                //           trendLinesEnabled[getTrendlineIdentifier(dp.channelId, dp.deviceId)] !== false &&
                //           dp.data.length > 0
                //   )
                // :
                dataPoints.some((dp) => dp.data.length > 0),
            highchartsConfig: {
                time: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    timezoneOffset: getTimezoneOffset as any,
                },
                credits: {
                    enabled: false,
                },
                title: undefined,
                legend: {
                    enabled: false,
                },

                annotations: [
                    {
                        shapeOptions: {
                            src: alarmImg,
                        },
                        shapes: (bellDataPoint || []).map((i: any) => ({
                            type: 'image',
                            width: 10,
                            height: 10,
                            draggable: 'xy',
                            point: {
                                x: i.isNewAlarm === true && i.alarmCount !== 0 ? new Date(i.dateTime).getTime() : 0,
                                y: i.isNewAlarm === true && i.alarmCount !== 0 ? parseFloat(i.value) : 0,
                                xAxis: 0,
                                yAxis: 0,
                            },
                        })),
                        draggable: '',
                    },
                ],

                chart: {
                    //zoomType: props.large ? 'x' : undefined,
                    animation: {
                        duration: 0,
                    },
                    height: !props.large ? 200 : '45%', // 16:9 ratio
                    alignTicks: false,
                    // resetZoomButton: {
                    //     theme: {
                    //         style: {
                    //             display: 'none',
                    //         },
                    //     },
                    // },
                    panKey: 'shift',
                    panning: {
                        enabled: true,
                        type: 'x',
                    },
                },
                xAxis: {
                    tickInterval: props.tickInterval,
                    type: 'datetime',
                    dateTimeLabelFormats: getChartsAxisDateTimeLabelFormats(),
                    labels: {
                        style: {
                            fontFamily: 'Open Sans',
                        },
                    },
                    events: {
                        afterSetExtremes,
                    },
                },
                tooltip: {
                    dateTimeLabelFormats: getChartsTooltipDateTimeLabelFormats(),
                    style: {
                        fontFamily: 'Open Sans',
                    },
                    formatter: function(): string {
                        return `${dataPoints[this.series.index].deviceName}
                            <br/> 
                            
                            ${
                                props.Inactive
                                    ? new Date(this.x || '').toJSON().substring(0, 10)
                                    : formatDateTime(new Date(this.x || '').toString())
                            } 
                            <br/>
                            <span style="color:
                            ${
                                props.Inactive
                                    ? ChartColors[
                                          dataPoints[this.series.index].index === 1
                                              ? 0
                                              : dataPoints[this.series.index].index % ChartColors.length
                                      ]
                                    : ChartColors[this.series.index % ChartColors.length]
                            } 
                            ">● </span> ${this.series.name} 
                            : <b>${this.y}${dataPoints[this.series.index].unit}</b>`;
                    },
                },
                yAxis,
                plotOptions: {
                    line: {
                        animation: false,
                        connectNulls: true,
                    },
                },
                exporting: {
                    enabled: false,
                },
                series: (dataPoints || []).map(
                    (dataPoint): Highcharts.SeriesOptionsType => ({
                        type: 'line',
                        data: dataPoint.data,
                        marker: {
                            enabled: dataPoint.data.length > 1 ? false : true,
                            symbol: 'circle',
                        },
                        yAxis: yAxis.findIndex((a) => {
                            if (!a.labels) {
                                return false;
                            }
                            return a.labels.format === `{value}${dataPoint.unit}`;
                        }),
                        lineWidth: 1,
                        // visible:
                        //     props.isDevice &&
                        //     trendLinesEnabled[getTrendlineIdentifier(dataPoint.channelId, dataPoint.deviceId)] !==
                        //         false,
                        zIndex:
                            hoveredTrendline &&
                            hoveredTrendline.channelId === dataPoint.channelId &&
                            hoveredTrendline.deviceId === dataPoint.deviceId
                                ? 999
                                : 1,
                        name: dataPoint.label,
                        color: props.Inactive
                            ? dataPoint.index
                                ? ChartColors[dataPoint.index === 1 ? 0 : dataPoint.index % ChartColors.length]
                                : 'rgb(77, 172, 246)'
                            : dataPoint.color,
                        tooltip: {
                            valueSuffix: dataPoint.unit,
                        },
                        showInLegend: true,
                    })
                ),
            },
        };
    }, [trendLinesEnabled, props.large, props.trendsData, hoveredTrendline, formatDateTime]);

    React.useEffect(() => {
        if (!isDataLoaded(props.trendsData)) {
            return undefined;
        }

        const chart = chartRef.current ? chartRef.current.chart : undefined;

        props.trendsData.value.forEach((trend, index) => {
            if (chart) {
                chart.series[index].setState(
                    hoveredTrendline &&
                        trend.id === hoveredTrendline.channelId &&
                        trend.deviceId === hoveredTrendline.deviceId
                        ? 'select'
                        : hoveredTrendline === undefined
                        ? 'normal'
                        : 'inactive'
                );
            }
        });
    }, [hoveredTrendline, props.trendsData, chartRef]);

    React.useEffect(() => {
        if (props.disableDownload && !props.isDevice) {
            props.disableDownload(!hasDataPoints);
        }
    }, [hasDataPoints]);

    const ChartComponent = highchartsConfig ? (
        <>
            <>
                {!someChannelsEnabled && (
                    <div
                        style={{
                            width: props.large ? '730px' : '360px',
                            height: props.large ? '400px' : '200px',
                            display: 'flex',
                            justifyContent: 'center',
                            // flex:1
                        }}
                    >
                        <DataEmptyIndicator
                            icon="ShowChart"
                            message={t('DASHBOARD.NO_CHANNELS_ENABLED')}
                            data={props.trendsData}
                            isEmpty
                            inline
                        />
                    </div>
                )}

                {someChannelsEnabled && (
                    <HighchartsReact ref={chartRef} highcharts={Highcharts} options={highchartsConfig} />
                )}
            </>
        </>
    ) : null;

    const trendsExportLoading = useSelector(Selectors.TrendExport.trendExportLoading);

    if (!props.large) {
        return (
            <>
                {hasDataPoints && ChartComponent}

                <DataEmptyIndicator
                    icon="ShowChart"
                    message={t('DASHBOARD.NO_DATA_POINTS')}
                    data={props.trendsData}
                    isEmpty={!hasDataPoints}
                    inline
                    style={{ height: '160px' }}
                />
            </>
        );
    }

    return (
        <List
            disablePadding
            subheader={
                props.Inactive ? (
                    ''
                ) : (
                    <ReusableTrendsCardHeader
                        title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                        isDevice={props.isDevice}
                        fetchTrendData={props.fetchTrendData}
                        //count={props.count}
                        large={props.large}
                    >
                        <ZoomOutButton
                            enabled={zoomOutButtonVisible}
                            onClick={(): void => {
                                const chart = chartRef.current ? chartRef.current.chart : undefined;
                                if (chart) {
                                    chart.zoomOut();
                                    setZoomOutButtonVisible(false);
                                }
                            }}
                        />
                        <IconButton
                            data-testid={'export-data-button'}
                            onClick={handleClickOpen}
                            disabled={
                                props.isDevice ? !someChannelsEnabled || trendsExportLoading : trendsExportLoading
                            }
                            size="large"
                        >
                            <Icon.GetApp />
                        </IconButton>
                    </ReusableTrendsCardHeader>
                )
            }
        >
            {isDataLoading(props.trendsData) && <TrendsGraphContentLoader style={{ height: '400px' }} />}

            {isDataLoaded(props.trendsData) && hasDataPoints && <>{ChartComponent}</>}

            {props.isDevice ? (
                <>
                    {open && props.channels && props.deviceId && props.isDevice === true && (
                        <TrendExportModal
                            open={open}
                            onClose={handleClose}
                            channels={props.channels}
                            deviceId={props.deviceId}
                            downloadCSV={props.downloadCSV}
                            isDevice={props.isDevice}
                        />
                    )}
                </>
            ) : (
                <>
                    <TrendDataProvider siteId={props.siteId}>
                        {open && isDataLoaded(props.exportChannel) && props.isDevice === undefined && (
                            <DeviceMasterProvider>
                                <TrendExportModal
                                    open={open}
                                    onClose={handleClose}
                                    //channels={props.channels}
                                    //deviceId={props.deviceId}
                                    downloadCSV={props.downloadCSV}
                                    isDevice={props.isDevice}
                                    trends={props.exportChannel.value}
                                />
                            </DeviceMasterProvider>
                        )}
                    </TrendDataProvider>
                </>
            )}
            <DataEmptyIndicator
                icon="ShowChart"
                message={t('DASHBOARD.NO_DATA_POINTS')}
                data={props.trendsData}
                isEmpty={!hasDataPoints}
                inline
                style={{ height: '360px' }}
            />
        </List>
    );
};
